import React, { useEffect, useMemo, useRef, useState } from "react";
import { SuggestionKeyDownProps } from "@tiptap/suggestion";
import { VariableDef } from "../variable-input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEntityInstanceLayoutStore } from "@/stores/entity-instance-field-store";
import { entityFieldsAsVariables } from "@/lib/variable-type-utils";

export interface VariableCommandRef {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
  handleCharacterInput: (char: string) => void;
}

export interface VariableCommandProps {
  items: VariableDef[];
  command: (item: VariableDef) => void;
  onClose?: () => void;
  /** Pick an array itself instead of drilling into one element (iterator sources). */
  selectArrays?: boolean;
}

export const VariableCommand = React.forwardRef<VariableCommandRef, VariableCommandProps>(
  ({ items, command, onClose, selectArrays }, ref) => {
    const [searchStr, setSearchStr] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const commandListRef = useRef<HTMLDivElement>(null);

    // when selecting a variable that has childs, we store the source path here
    const [variablePath, setVariablePath] = useState<string[]>([]);
    const [variableDisplayNamePath, setVariableDisplayNamePath] = useState<string[]>([]);

    //  we use this for multi level variables so we can set the current rendered items
    const [internalItems, setInternalItems] = useState<VariableDef[]>(items);

    // Sync internalItems with items prop, but only if we're not in navigation state
    useEffect(() => {
      // Only reset if we're at the root level (no variablePath)
      if (variablePath.length === 0) {
        setInternalItems(items);
      }
    }, [items, variablePath.length]);

    const [displayFielsForEntityName, setDisplayFielsForEntityName] = useState<string>("");
    const [scalarOnlyDrill, setScalarOnlyDrill] = useState(false);

    const { entitySchema } = useEntityInstanceLayoutStore(displayFielsForEntityName ?? "");

    // Store the navigation history for going back
    const [navigationHistory, setNavigationHistory] = useState<VariableDef[][]>([items]);

    const handleGoBack = () => {
      if (displayFielsForEntityName) {
        // Going back from entity fields view
        setDisplayFielsForEntityName("");
        setScalarOnlyDrill(false);
        setVariablePath((prev) => prev.slice(0, -1));
        setVariableDisplayNamePath((prev) => prev.slice(0, -1));
        setSearchStr("");
        setSelectedIndex(0);
        return;
      }
      if (variablePath.length > 0) {
        // Going back in the tree
        setVariablePath((prev) => prev.slice(0, -1));
        setVariableDisplayNamePath((prev) => prev.slice(0, -1));
        setNavigationHistory((prev) => prev.slice(0, -1));
        setInternalItems(navigationHistory[navigationHistory.length - 2] || items);
        setSearchStr("");
        setSelectedIndex(0);
      }
    };

    const handleSelect = (variable: VariableDef) => {
      if (selectArrays && variable.isArray) {
        command({
          ...variable,
          name: [...variablePath, variable.name].join("."),
          displayName: [...variableDisplayNamePath, variable.displayName].join(" > "),
        });
        return;
      }
      if (variable.entityName) {
        setVariableDisplayNamePath((prev) => [...prev, variable.displayName]);
        setVariablePath((prev) => [...prev, variable.name]);
        setDisplayFielsForEntityName(variable.entityName);
        setScalarOnlyDrill(!!variable.scalarFieldsOnly);
        setSearchStr("");
        setSelectedIndex(0);
        return;
      }
      if (variable.children && variable.children.length > 0) {
        setSelectedIndex(0);
        setNavigationHistory((prev) => [...prev, variable.children!]);
        setInternalItems(variable.children);
        // Children of an array describe one element, so the path needs an index
        setVariablePath((prev) => [
          ...prev,
          variable.isArray ? `${variable.name}[0]` : variable.name,
        ]);
        setVariableDisplayNamePath((prev) => [...prev, variable.displayName]);
        setSearchStr("");
        return;
      }
      command({
        ...variable,
        name: [...variablePath, variable.name].join("."),
        displayName: [...variableDisplayNamePath, variable.displayName].join(" > "),
      });
      // Note: Don't call onClose here - TipTap's suggestion system handles cleanup via onExit
      // Calling onClose would delete the just-inserted mention since onClose now removes the @ range
    };

    const canGoBack = variablePath.length > 0 || !!displayFielsForEntityName;

    const filteredVariables = useMemo(() => {
      if (displayFielsForEntityName) {
        const fields = entityFieldsAsVariables(entitySchema?.fields, {
          scalarOnly: scalarOnlyDrill,
          search: searchStr,
        });
        if (!selectArrays) return fields;
        // A caller that wants arrays wants list relations as values, not as something to drill into.
        const listRelations = new Set(
          (entitySchema?.fields ?? [])
            .filter((f) => f.kind === "OBJECT" && f.list)
            .map((f) => f.name),
        );
        return fields.map((v) => (listRelations.has(v.name) ? { ...v, isArray: true } : v));
      }
      const filtered =
        internalItems.filter((variable) => {
          const searchLower = searchStr.toLowerCase();

          return (
            variable.name.toLowerCase().includes(searchLower) ||
            variable.displayName.toLowerCase().includes(searchLower)
          );
        }) || [];

      return filtered;
    }, [
      displayFielsForEntityName,
      entitySchema?.fields,
      internalItems,
      searchStr,
      scalarOnlyDrill,
    ]);

    useEffect(() => {
      setSelectedIndex(0);
    }, [filteredVariables, searchStr]);

    useEffect(() => {
      if (commandListRef.current) {
        const selectedElement = commandListRef.current.querySelector(
          `[data-index="${selectedIndex}"]`,
        ) as HTMLElement;

        if (selectedElement) {
          // Manual scroll calculation to only scroll within the CommandList container
          // This prevents scrollIntoView from scrolling the entire popup
          const container = commandListRef.current;
          const containerRect = container.getBoundingClientRect();
          const elementRect = selectedElement.getBoundingClientRect();

          // Check if element is below visible area
          if (elementRect.bottom > containerRect.bottom) {
            container.scrollTop += elementRect.bottom - containerRect.bottom;
          }
          // Check if element is above visible area
          else if (elementRect.top < containerRect.top) {
            container.scrollTop -= containerRect.top - elementRect.top;
          }
        }
      }
    }, [selectedIndex]);

    const selectedVariableName = filteredVariables[selectedIndex]?.name || "";

    React.useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: SuggestionKeyDownProps) => {
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          setSearchStr((prev) => prev + event.key);
          return true;
        }

        if (event.key === "Enter") {
          if (filteredVariables.length > 0 && filteredVariables[selectedIndex]) {
            handleSelect(filteredVariables[selectedIndex]);
          }
          return true;
        }

        if (event.key === "ArrowDown") {
          setSelectedIndex((prev) => (prev < filteredVariables.length - 1 ? prev + 1 : prev));
          return true;
        }

        if (event.key === "ArrowUp") {
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          return true;
        }

        if (["ArrowLeft", "ArrowRight"].includes(event.key)) {
          return true;
        }

        if (event.key === "Backspace") {
          if (searchStr.length > 0) {
            setSearchStr((prev) => prev.slice(0, -1));
          } else if (canGoBack) {
            handleGoBack();
          } else {
            // At root with empty search, close popup and remove @
            onClose?.();
          }
          return true;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          if (canGoBack) {
            handleGoBack();
          } else {
            onClose?.();
          }
          return true;
        }

        return false;
      },

      handleCharacterInput: (char: string) => {
        setSearchStr((prev) => prev + char);
      },
    }));

    return (
      <Command filter={() => 1} className="border border-gray-200" value={selectedVariableName}>
        {canGoBack && (
          <div
            className="flex cursor-pointer items-center gap-1 border-b bg-gray-50 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleGoBack();
            }}
          >
            <ChevronLeft className="h-4 w-4" />
            <span>{variableDisplayNamePath.join(" > ") || "Back"}</span>
          </div>
        )}
        <CommandInput
          placeholder="Search variables..."
          value={searchStr}
          onValueChange={setSearchStr}
        />
        <CommandList ref={commandListRef} className="!max-h-[250px]">
          <CommandEmpty className="py-2">No variables found.</CommandEmpty>
          <CommandGroup>
            {filteredVariables.map((variable, index) => (
              <CommandItem
                key={variable.name}
                value={variable.name}
                data-index={index}
                // TODO: should we really use mouse down? or can we use click?
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  handleSelect(variable);
                }}
                onMouseEnter={() => {
                  setSelectedIndex(index);
                }}
                className="flex cursor-pointer items-center justify-between gap-y-0"
              >
                <div>
                  <div className="font-medium">{variable.displayName}</div>
                  <div className="text-xs text-muted-foreground">{variable.description}</div>
                </div>
                {!(selectArrays && variable.isArray) &&
                  ((variable.children && variable.children.length > 0) || variable.entityName) && (
                    <div className="">
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  )}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    );
  },
);

VariableCommand.displayName = "VariableCommand";

export default VariableCommand;
